import React, { useRef, useState } from 'react';
import cn from 'classnames';

import { Icons, SpinnerOverlay } from '@components/common';
import ActionsMenu from '@components/common/ActionsMenu';

import { useDownloadFile } from '@hooks/common/useDownloadFile';
import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { editMenuActions, EEditMenuActions } from './constants';

import styles from './Logo.module.scss';

interface ILogoProps {
	openLogoModal: () => void;
	removeLogo: () => void;
	avatarId?: string | null;
	description?: string;
	isRequired?: boolean;
	isLogoSquare?: boolean;
}

const Logo = ({
	openLogoModal,
	removeLogo,
	avatarId,
	description = 'Organisation logo',
	isRequired,
	isLogoSquare,
}: ILogoProps) => {
	const editMenuWrapperRef = useRef<HTMLDivElement | null>(null);

	const [isVisibleEditActionMenu, setIsVisibleEditActionMenu] = useState(false);

	const toggleVisibleEditActionMenu = () => setIsVisibleEditActionMenu(prevState => !prevState);

	useOnClickOutside({ refs: [editMenuWrapperRef], handler: () => setIsVisibleEditActionMenu(false) });

	const {
		downloadUrl,
		isLoadingDownloadURL,
	} = useDownloadFile({ id: avatarId });

	const onClickEditMenuItem = (actionId: string) => {
		if (actionId === EEditMenuActions.upload) {
			openLogoModal();
		}

		if (actionId === EEditMenuActions.clear) {
			removeLogo();
		}
	};

	const onUploadLogo = () => {
		if (downloadUrl) {
			return;
		}

		openLogoModal();
	};

	return (
		<div
			className={styles.logoContainer}
			data-testid='logoContainer'
		>
			<div className={cn(styles.description, { [styles.isRequired]: isRequired })}>
				{description}
			</div>
			<div
				className={cn(styles.logo, { [styles.withoutImage]: !downloadUrl })}
				data-testid='logo-action'
				onClick={onUploadLogo}
			>
				{isLoadingDownloadURL && <SpinnerOverlay />}
				{downloadUrl
					? <img
						alt={description}
						src={downloadUrl}
						style={{
							borderRadius: isLogoSquare ? '4px' : '50%',
						}}
					/>
					: isLogoSquare ? Icons.logoStubSquare() : Icons.logoStub()
				}
				<div className={styles.uploadIcon}>
					{Icons.uploadAvatarIcon()}
				</div>
				{downloadUrl
					&& <div
						ref={editMenuWrapperRef}
						className={styles.editButton}
						onClick={toggleVisibleEditActionMenu}
					>
						{Icons.actions()}
						<ActionsMenu
							actions={editMenuActions}
							isVisibleMenu={isVisibleEditActionMenu}
							minWidth={120}
							onClick={onClickEditMenuItem}
						/>
					</div>
				}
			</div>
		</div>
	);
};

export default Logo;
